import "swiper/css";

import classNames from "classnames";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Autoplay, Keyboard } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperClass from "swiper/types/swiper-class";

import IconPause from "@/icons/icon-pause";
import IconPlay from "@/icons/icon-play";
import Container from "~/components/common/container";
import Icon from "~/components/common/icon";
import { useTextDir } from "~/contexts/text-dir";
import Editorials from "~/types/editorials";
import AppRoutes from "~/utils/app-routes";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";

import styles from "./hero.module.scss";
import HeroSlide from "./hero-slide";
import useHeroScrollHintScheduler from "./use-hero-scroll-hint-scheduler";

type Props = {
  entry: Entry<unknown>;
  enabled?: boolean;
};

export default function Hero({ entry, enabled = true }: Props) {
  const dir = useTextDir();
  const t = useTranslations();
  const router = useRouter();

  const swiperRef = useRef<SwiperClass>();
  const [autoplayStatus, setAutoplayStatus] = useState(true);
  const [realIndex, setRealIndex] = useState(0);

  // Owns the shared scroll-hint timers for the whole page - anchored here (the swiper
  // container) rather than inside each HeroScrollHint slide instance, since swiper's loop
  // autoplay remounts slide children on every rotation, which would otherwise restart the
  // appear countdown from zero forever.
  useHeroScrollHintScheduler(
    !CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_DISABLE_HERO_HINT) && router.pathname === AppRoutes.HOMEPAGE
  );

  useEffect(() => {
    if (dir && swiperRef.current) {
      swiperRef.current.changeLanguageDirection(dir);
    }
  }, [dir]);

  useEffect(() => {
    if (enabled) {
      swiperRef.current?.enable();
    } else {
      swiperRef.current?.disable();
    }
  }, [enabled]);

  if (!isActiveEntry(entry)) return null;

  assertEditorialType<Editorials.Hero>(entry, "hero");

  const activeSlides = entry.fields.slides.filter(isActiveEntry);

  return (
    <div className={classNames("beacon-hero", styles.container)}>
      <Swiper
        modules={[Autoplay, Keyboard]}
        keyboard
        loop
        wrapperTag="ul"
        autoplay={{ delay: entry.fields.duration * 1000 }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        onAutoplayStart={(swiper) => setAutoplayStatus(swiper.autoplay.running)}
        onAutoplayStop={(swiper) => setAutoplayStatus(swiper.autoplay.running)}
        onSlideChange={(swiper) => setRealIndex(swiper.realIndex)}
        enabled={enabled}
        speed={Constants.SWIPER_TRANSITION_DURATION}
      >
        {activeSlides.map((slide, index) => (
          <SwiperSlide key={slide.sys.id} tag="li">
            {({ isActive }) => <HeroSlide entry={slide} noLazy={index == 0} isActive={isActive} isHeroSlider />}
          </SwiperSlide>
        ))}
      </Swiper>
      {entry.fields.slides.filter(isActiveEntry).map(
        (slide, index) =>
          realIndex == index && (
            <div
              key={`id-` + index}
              className={styles.bulletsContainer}
              aria-current={realIndex == index ? true : undefined}
            >
              <div className={classNames(styles.bulletsWrapper)}>
                <div
                  className={classNames(styles.carouselBullets, { [styles.autoplayBullets]: autoplayStatus })}
                  style={{ "--autoplay-duration": `${entry.fields.duration * 1000}ms` } as React.CSSProperties}
                >
                  {entry.fields.slides.filter(isActiveEntry).map((slide, i) => (
                    <button
                      key={slide.sys.id}
                      className={classNames(
                        styles.bullet,
                        realIndex == i ? (autoplayStatus ? styles.bulletAutoplayActive : styles.bulletActive) : ""
                      )}
                      onClick={() => swiperRef.current?.slideToLoop(i)}
                      aria-label={t("a11y.goToSlideOf", { to: i + 1, of: activeSlides.length })}
                    />
                  ))}
                </div>
                <button
                  className={styles.pause}
                  onClick={() => swiperRef.current?.autoplay?.[autoplayStatus ? "stop" : "start"]()}
                  aria-label={t(autoplayStatus ? "a11y.pause" : "a11y.play")}
                  aria-pressed={!autoplayStatus}
                >
                  <Icon icon={autoplayStatus ? IconPause : IconPlay} color="black" aria-hidden />
                </button>
              </div>
            </div>
          )
      )}
    </div>
  );
}
